// React
import React, { Component, PropTypes } from 'react';

// Components
import Swipeable from 'react-swipeable';

// CSS
import classNames from 'classnames';
import prefixClassNames from 'SharedComponents/prefixClassNames';
import filterProps from 'SharedComponents/filterProps';
import './Carousel.less';

@prefixClassNames()
export default class Carousel extends Component {

    static propTypes = {
        className: PropTypes.string,
        style: PropTypes.any,
        id: PropTypes.any,

        /* Component to render props with */
        component: PropTypes.any.isRequired,

        /* An array of props to pass two each component */
        propSets: PropTypes.array.isRequired,

        /* Function to call when a "prev" event occurs */
        onPrev: PropTypes.func,

        /* Function to call when a "next" event occurs */
        onNext: PropTypes.func,

        /* Function to call when the current slide changes: onChange(e, propSetIndex) */
        onChange: PropTypes.func,

        /** Enable swipe events (swiping left/right triggers next/previous match) */
        enableSwipe: PropTypes.bool,

        /** Enable automatic scrolling (a value in milliseconds or false to disable) */
        autoNext:  PropTypes.oneOfType([PropTypes.number, PropTypes.bool]),

        /* Unique key to check in propSets, to match the current propSet when propSets change */
        uniqueKey: PropTypes.string,

        //
        onMouseOver: PropTypes.func,
        onMouseOut: PropTypes.func,
        flickThreshold: PropTypes.number,
        debounce: PropTypes.number,
        children: PropTypes.node,
    }

    static defaultProps = {

        onPrev: () => {},
        onNext: () => {},

        onChange: (propSet, propSetIndex) => ({ propSet, propSetIndex }),
        autoNext: 5000,
        flickThreshold: 100, // pixels
        debounce: 500, // ms
        enableSwipe: true,

        uniqueKey: 'id',

        component: 'div',
        propSets: [],
    }

    constructor(props, context) {
        super(props, context);

        this.state = {
            currentIndex: 0,
            transitionClass: 'SlideNone',
            autoNextInterval: 0,
        };

        this.lastDebounce = Date.now();
        this.allowUpdates = true;
    }

    componentWillMount() {
        this.enableAutoNext(this.props, this.state);

        if (this.props.propSets && this.props.propSets.length) {
            this.props.onChange(this.props.propSets[this.state.currentIndex], this.state.currentIndex);
        }
    }

    shouldComponentUpdate(nextProps, nextState) {
        if (!this.allowUpdates) {
            return false;
        }

        for (const key of [].concat(Object.keys(this.props || {})).concat(Object.keys(nextProps || {}))) {
            if (this.props[key] !== nextProps[key]) {
                return true;
            }
        }

        for (const key of [].concat(Object.keys(this.state || {})).concat(Object.keys(nextState || {}))) {
            if (this.state[key] !== nextState[key]) {
                return true;
            }
        }

        return false;
    }

    componentWillUpdate(nextProps, nextState) {
        // autoNext Updates
        if (this.props.autoNext !== nextProps.autoNext) {
            clearInterval(this.state.autoNextInterval);
            this.enableAutoNext(nextProps, nextState);
        }
        // different propSets
        if (nextProps.propSets &&
            this.props.propSets[nextState.currentIndex] !== nextProps.propSets[nextState.currentIndex] &&
            this.props.propSets[nextState.currentIndex] && nextProps.propSets[nextState.currentIndex] &&
            this.props.propSets[nextState.currentIndex][nextProps.uniqueKey] !== nextProps.propSets[nextState.currentIndex][nextProps.uniqueKey]
        ) {
            let currentIndex = 0;
            for (let i = 0; i < nextProps.propSets.length; i++) {
                if (
                    this.props.propSets[nextState.currentIndex] === nextProps.propSets[i] ||
                    (
                        nextProps.uniqueKey &&
                        this.props.propSets[nextState.currentIndex][nextProps.uniqueKey] &&
                        this.props.propSets[nextState.currentIndex][nextProps.uniqueKey] === nextProps.propSets[i][nextProps.uniqueKey]
                    )
                ) {
                    currentIndex = i;
                    break;
                }
            }
            if (currentIndex !== nextState.currentIndex) {
                this.setState({ currentIndex, transitionClass: 'SlideNone' });
                this.props.onChange(nextProps.propSets[currentIndex], currentIndex);
            }
        }
    }

    componentDidUpdate() {
        if (this.completeTransition) {
            this.completeTransition = false;

            // This is actually intended
            (requestAnimationFrame || setTimeout)(() => { // first one is reserved for React updates batching strategy
                (requestAnimationFrame || setTimeout)(() => { // second one guarantees DOM is up-to-date before us
                    if (this.noStateUpdates) {
                        return;
                    }
                    this.carouselContents.className = this.animateTransformClasses.className;

                    this.newTransform = '';
                    this.prevSlide.style.transform = this.newTransform;
                    this.currentSlide.style.transform = this.newTransform;
                    this.nextSlide.style.transform = this.newTransform;
                    return;
                }, 1000 / 60);
            }, 1000 / 60);
        }
    }

    componentWillUnmount() {
        this.noStateUpdates = true;
        clearInterval(this.state.autoNextInterval);
    }

    onPrevClick(e) {
        const now = Date.now();
        if (now - this.lastDebounce < this.props.debounce) {
            return false;
        }
        this.lastDebounce = now;

        const currentIndex = this.state.currentIndex - 1 < 0 ? this.props.propSets.length - 1 : this.state.currentIndex - 1;
        const transitionClass = this.state.transitionClass === 'SlideRight' && 'SlideRightAlt' || 'SlideRight';
        this.setState({ currentIndex, transitionClass });
        return (this.props.onChange(this.props.propSets[currentIndex], currentIndex) || 1) && this.props.onPrev(e);
    }

    onNextClick(e) {
        const now = Date.now();
        if (now - this.lastDebounce < this.props.debounce) {
            return false;
        }
        this.lastDebounce = now;
        const currentIndex = this.state.currentIndex + 1 > this.props.propSets.length - 1 ? 0 : this.state.currentIndex + 1;
        const transitionClass = this.state.transitionClass === 'SlideLeft' && 'SlideLeftAlt' || 'SlideLeft';
        this.setState({ currentIndex, transitionClass });
        return (this.props.onChange(this.props.propSets[currentIndex], currentIndex) || 1) && this.props.onNext(e);
    }

    onSwiping(dir = 'right', e, delta) {
        if (this.carouselContents.className !== this.slideNoneClasses.className) {
            this.carouselContents.className = this.slideNoneClasses.className;
        }

        clearInterval(this.state.autoNextInterval);
        const vario = delta / this.carouselContents.clientWidth > 1 && 1 || delta / this.carouselContents.clientWidth;
        this.lastVario = vario;
        const translate = dir === 'left' && (-100 - (vario * 100)) || (-100 + (vario * 100));
        this.newTransform = `translate(${translate}%)`;

        this.prevSlide.style.transform = this.newTransform;
        this.currentSlide.style.transform = this.newTransform;
        this.nextSlide.style.transform = this.newTransform;
    }

    onSwiped(dir = 'right', e, x, y, isFlick) {
        this.enableAutoNext(this.props, this.state);
        if (this.lastVario > 0.5 || (isFlick && this.lastVario)) {
            const newIndex = dir === 'left' ?
                this.state.currentIndex + 1 > this.props.propSets.length - 1 ? 0 : this.state.currentIndex + 1 :
                this.state.currentIndex - 1 < 0 ? this.props.propSets.length - 1 : this.state.currentIndex - 1;

            // Reposition to complete the animation
            const translate = dir === 'left' && (0 - (this.lastVario * 100)) || (-200 + (this.lastVario * 100));
            this.newTransform = `translate(${translate}%)`;
            this.completeTransition = true;

            // State Update - transitionClass is out of sync at this point
            this.carouselContents.className = this.slideNoneClasses.className;
            this.setState({ currentIndex: newIndex, transitionClass: 'SlideNone' });

            this.lastVario = 0;
            return (this.props.onChange(this.props.propSets[newIndex], newIndex) || 1) && (dir === 'left' && (this.props.onNext(e) || 1) || this.props.onPrev(e));
        }
        this.newTransform = '';

        this.prevSlide.style.transform = this.newTransform;
        this.currentSlide.style.transform = this.newTransform;
        this.nextSlide.style.transform = this.newTransform;

        this.prevSlide = false;
        this.currentSlide = false;
        this.nextSlide = false;

        this.setState({ transitionClass: 'AnimateTransform' });
        this.lastVario = 0;

        return false;
    }

    enableAutoNext(props) {
        const { autoNext, propSets } = props;
        clearInterval(this.state.autoNextInterval);
        if (autoNext && !isNaN(Number(autoNext)) && autoNext > 0 && propSets && propSets.length && propSets.length > 1) {
            this.setState({ autoNextInterval: setInterval(() => { this.onNextClick(); }, autoNext) });
        }
    }

    render() {
        const { className, propSets, component, onMouseOver, onMouseOut, enableSwipe, flickThreshold, id, style } = this.props;
        const { currentIndex, transitionClass } = this.state;
        const prevIndex = currentIndex - 1 < 0 ? propSets.length - 1 : currentIndex - 1;
        const nextIndex = currentIndex + 1 > propSets.length - 1 ? 0 : currentIndex + 1;
        const TagName = component;

        const newProps = {
            delta: 0,
            onMouseOver: (e) => {
                clearInterval(this.state.autoNextInterval);
                return onMouseOver && onMouseOver(e);
            },
            onMouseOut: (e) => {
                this.enableAutoNext(this.props, this.state);
                return onMouseOut && onMouseOut(e);
            },
            onSwipingRight: (e, delta) => enableSwipe && propSets.length > 1 && this.onSwiping('right', e, delta),
            onSwipingLeft: (e, delta) => enableSwipe && propSets.length > 1 && this.onSwiping('left', e, delta),

            onSwiped: (e, x, y, isFlick) => {
                if (!enableSwipe || this.props.propSets.length < 2) {
                    return false;
                }
                const isLegalFlick = isFlick && Math.abs(x) > flickThreshold;
                return (x < 0) && (this.onSwiped('right', e, x, y, isLegalFlick) || 1) || (this.onSwiped('left', e, x, y, isLegalFlick) || 1);
            },

            className: classNames(className, { Carousel: true }),
            id,
            style,
        };

        let hasPrevRef = false;
        let hasCurrentRef = false;
        let hasNextRef = false;

        return propSets && propSets.length && (
            <Swipeable {...newProps}>
                {propSets.length > 1 && (
                    <div className="CarouselControls">
                        <a className="CarouselControlPrev" onClick={() => this.onPrevClick()} />
                        <a className="CarouselControlNext" onClick={() => this.onNextClick()} />
                    </div>
                ) || null}
                <span
                    ref={c => { this.animateTransformClasses = c; }}
                    className={classNames({
                        CarouselContents: true,
                        SlideRight: false,
                        SlideRightAlt: false,
                        SlideLeft: false,
                        SlideLeftAlt: false,
                        AnimateTransform: true,
                        SlideNone: false,
                    })}
                    style={{ position: 'absolute', display: 'none' }}
                />
                <span
                    ref={c => { this.slideNoneClasses = c; }}
                    className={classNames({
                        CarouselContents: true,
                        SlideRight: false,
                        SlideRightAlt: false,
                        SlideLeft: false,
                        SlideLeftAlt: false,
                        AnimateTransform: false,
                        SlideNone: true,
                    })}
                    style={{ position: 'absolute', display: 'none' }}
                />
                <div
                    ref={c => { this.carouselContents = c; }}
                    className={classNames({
                        CarouselContents: true,
                        [transitionClass]: !!transitionClass,
                    })}
                >
                    {currentIndex === 0 && (() => {
                        const key = propSets.length - 1;
                        const propSet = propSets && filterProps(propSets[key], this.constructor.propTypes) || {};
                        hasPrevRef = true;
                        return (
                            <div ref={c => { this.prevSlide = c; }} className="CarouselSlide" style={{ transform: this.newTransform }}>
                                <TagName {...(propSet || {})}>
                                    {propSet && propSet.children || []}
                                </TagName>
                            </div>
                        );
                    })() || null}
                    {propSets && propSets.map((propSet, key) => (
                        key === prevIndex || key === currentIndex || key === nextIndex
                    ) && (
                        currentIndex !== 0 || (currentIndex === 0 && key !== propSets.length - 1) || propSets.length === 2
                    ) && (
                        currentIndex !== propSets.length - 1 || (currentIndex === propSets.length - 1 && key !== 0) || propSets.length === 2
                    ) && (
                        <div
                            key={key}
                            ref={c => {
                                this[
                                    key === prevIndex && !hasPrevRef && (hasPrevRef = true) && 'prevSlide' ||
                                    key === currentIndex && !hasCurrentRef && (hasCurrentRef = true) && 'currentSlide' ||
                                    key === nextIndex && !hasNextRef && (hasNextRef = true) && 'nextSlide' || 'noneSlide'
                                ] = c;
                            }}
                            className="CarouselSlide"
                            style={{ transform: this.newTransform }}
                        >
                            <TagName {...(propSet && filterProps(propSet, this.constructor.propTypes) || {})}>
                                {propSet && propSet.children || []}
                            </TagName>
                        </div>
                    ) || null)}
                    {currentIndex === propSets.length - 1 && (() => {
                        const key = 0;
                        const propSet = propSets && filterProps(propSets[key], this.constructor.propTypes) || {};
                        hasNextRef = true;
                        return (
                            <div ref={c => { this.nextSlide = c; }} className="CarouselSlide" style={{ transform: this.newTransform }}>
                                <TagName {...(propSet || {})}>
                                    {propSet && propSet.children || []}
                                </TagName>
                            </div>
                        );
                    })() || null}
                </div>
                {this.props.children}
            </Swipeable>
        ) || null;
    }
}



// WEBPACK FOOTER //
// ./src/SharedComponents/Carousel.js